import { Injectable, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { CustomChemical } from '../data-models/custom-chemical.model';
import { CustomChemicalRequest } from '../request-models/custom-chemical-request.model';
import { toCustomChemicalRequest } from '../utils/custom-chemical-request-utils';

/**
 * The current organization's own chemicals: `/custom-chemicals[/:id]` (hub `user-custom-chemicals-controller.ts`).
 * The hub scopes every route to the organization the auth interceptor sends. The list is not paged.
 *
 * Holds the loaded chemicals as signal state. Failures set `error` (the hub's message) and resolve to
 * `null` / `false` rather than rejecting.
 */
@Injectable({
  providedIn: 'root'
})
export class CustomChemicalService extends BaseService<CustomChemical> {
  // Signal-based state management
  private chemicalsSignal = signal<CustomChemical[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals
  readonly chemicals = computed(() => this.chemicalsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  constructor() {
    super('custom-chemicals', CustomChemical);
  }

  /**
   * Load custom chemicals for current organization
   * GET /custom-chemicals?search=&category=
   */
  async loadChemicals(options: { search?: string; category?: string } = {}): Promise<void> {
    const query = { search: options.search || undefined, category: options.category || undefined };
    const chemicals = await this.track('Failed to load custom chemicals', () => firstValueFrom(this.getMany({ query })));
    if (chemicals) {
      this.chemicalsSignal.set(chemicals);
    }
  }

  /**
   * Get custom chemical by ID
   */
  async getChemicalById(id: string): Promise<CustomChemical | null> {
    return this.track('Failed to fetch custom chemical', () => firstValueFrom(this.get(id)));
  }

  /**
   * Create a new custom chemical
   */
  async createChemical(dto: CustomChemicalRequest): Promise<CustomChemical | null> {
    const created = await this.track('Failed to create custom chemical', () =>
      firstValueFrom(this.create(toCustomChemicalRequest(dto)))
    );
    if (created) {
      this.chemicalsSignal.update(chemicals => [...chemicals, created]);
    }
    return created;
  }

  /**
   * Update an existing custom chemical. Send `null` for an optional field the user emptied.
   */
  async updateChemical(id: string, dto: CustomChemicalRequest): Promise<CustomChemical | null> {
    const saved = await this.track('Failed to update custom chemical', () =>
      firstValueFrom(this.update(id, toCustomChemicalRequest(dto)))
    );
    if (saved) {
      this.chemicalsSignal.update(chemicals => chemicals.map(c => (c.id === id ? saved : c)));
    }
    return saved;
  }

  /**
   * Delete a custom chemical (soft-delete)
   */
  async deleteChemical(id: string): Promise<boolean> {
    const deleted = await this.track('Failed to delete custom chemical', async () => {
      await firstValueFrom(this.delete(id));
      return true;
    });
    if (deleted) {
      this.chemicalsSignal.update(chemicals => chemicals.filter(c => c.id !== id));
    }
    return deleted ?? false;
  }

  /**
   * Search custom chemicals (client-side filtering)
   */
  searchChemicals(query: string): CustomChemical[] {
    if (!query || query.trim().length < 2) {
      return this.chemicalsSignal();
    }

    const searchTerm = query.toLowerCase().trim();
    return this.chemicalsSignal().filter(c =>
      c.name?.toLowerCase().includes(searchTerm) ||
      c.epaNumber?.toLowerCase().includes(searchTerm) ||
      c.activeIngredient?.toLowerCase().includes(searchTerm) ||
      c.manufacturer?.toLowerCase().includes(searchTerm)
    );
  }

  /**
   * Get chemicals by category (client-side filtering)
   */
  getChemicalsByCategory(category: string): CustomChemical[] {
    return this.chemicalsSignal().filter(c => c.category === category);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /**
   * Runs an action with the loading flag set. A failure records the hub's message (or `fallback`) in `error`
   * and resolves to `null`.
   */
  private async track<R>(fallback: string, action: () => Promise<R>): Promise<R | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      return await action();
    } catch (error: unknown) {
      const message = ApiError.fromHttpError(error).errors[0] ?? fallback;
      console.error(`[CustomChemicalService] ${fallback}:`, message);
      this.errorSignal.set(message);
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }
}
